import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Link, useNavigate } from 'react-router-dom'
import AuthLayout from '../layout'
import routesPath from '@/utils/routesPath'
import { toast } from 'sonner'
import type { Dictionary } from '@/types'
import { useFormik } from 'formik'
import { forgotPasswordSchema } from '@/validation-schemas/auth/login'
import {
  useForgotPasswordMutation,
  useResendOTPMutation,
  useVerifyPasswordResetOTPMutation,
} from '@/redux/services/auth/authApi'
import { useState } from 'react'
import { InputOTPGenerator } from '@/components/inputs/otp-generator'
import { InlineLoader } from '@/components/loader/custom-loader'

const { LOGIN, RESET_PASSWORD } = routesPath

export default function ForgotPasswordPage() {
  const [verifyPassword, setVerifyPassword] = useState(false)
  const [OTP, setOTP] = useState<any>('')

  const navigate = useNavigate()

  const [forgotPassword, { isLoading }] = useForgotPasswordMutation()
  const [resendOtp, { isLoading: loadingResendOtp }] = useResendOTPMutation()

  const [verifyOTP, { isLoading: isVerifying }] =
    useVerifyPasswordResetOTPMutation()

  const handleOTPSubmit = async (email: string) => {
    const resetPayload = { email, code: OTP }
    sessionStorage.setItem('reset-password-data', JSON.stringify(resetPayload))
    navigate(RESET_PASSWORD, {
      state: resetPayload,
    })
  }

  const handleResendOtp = async (email: string) => {
    try {
      const res = await resendOtp({ email }).unwrap()
      if (res.status) {
        toast.success(`${res.message}`)
      }
    } catch (error: any) {
      toast.error(`${error.message ?? 'Check your network and try again.'}`)
    }
  }

  const handleFormSubmit = async (values: Dictionary) => {
    forgotPassword(values)
      .unwrap()
      .then((res) => {
        toast.success(res.message)
        // navigate(RESET_PASSWORD)
        setVerifyPassword(true)
      })
  }

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: forgotPasswordSchema,
    onSubmit: handleFormSubmit,
  })

  return (
    <AuthLayout
      title='Forgot Password'
      subTitle='Enter your email and we will send you a code to reset your password'
      hasBack
    >
      {!verifyPassword ? (
        <form onSubmit={formik.handleSubmit}>
          <div className='flex flex-col gap-3'>
            <Input
              id='email'
              label='Email'
              name='email'
              onChange={formik.handleChange}
              value={formik.values.email}
              touched={formik.touched.email}
              error={formik.errors.email}
              placeholder='admin@example.com'
            />
          </div>

          <div className='my-4 mt-10'>
            <Button
              type='submit'
              disabled={!(formik.isValid || formik.dirty) || isLoading}
              loading={isLoading}
            >
              Send Code
            </Button>
            <span className='text-xs text-[#A3A3A3] block text-center pt-3'>
              Remember Password?{' '}
              <Link to={LOGIN} className='text-primary underline'>
                Login
              </Link>
            </span>
          </div>
        </form>
      ) : (
        <div>
          <div className='flex flex-col gap-3'>
            <InputOTPGenerator length={6} onChange={(code) => setOTP(code)} />
          </div>
          <span className='flex gap-1 items-center text-xs text-[#A3A3A3] justify-center text-center pt-3'>
            <span>Didn't recieve code?</span>
            <span
              className='text-green-700 cursor-pointer flex items-center gap-1'
              onClick={() => handleResendOtp(formik.values.email)}
            >
              {!loadingResendOtp ? (
                'Resend code'
              ) : (
                <div className='flex flex-row gap-x-1 items-center'>
                  <span className='animate-pulse'>Sending...</span>
                  <InlineLoader />
                </div>
              )}
            </span>
          </span>

          <div className='my-4 mt-10'>
            <Button
              type='button'
              disabled={isVerifying || OTP.length < 6 || loadingResendOtp}
              loading={isVerifying}
              onClick={() => handleOTPSubmit(formik.values.email)}
            >
              Verify Code
            </Button>
          </div>
        </div>
      )}
    </AuthLayout>
  )
}
